import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { purchaseHistoryApi } from '../../api/purchaseHistoryApi';
import { getArtworkImageSrc } from '../../utils/artworkImage';

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const formatPrice = (price) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price);

const getStatusColor = (status) => {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    processing: 'bg-blue-100 text-blue-800',
    shipped: 'bg-purple-100 text-purple-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const MyPurchasesTab = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [historyRes, statsRes] = await Promise.all([
          purchaseHistoryApi.getPurchaseHistory(),
          purchaseHistoryApi.getPurchaseStats(),
        ]);
        if (historyRes.success) setOrders(historyRes.orders || []);
        if (statsRes.success) setStats(statsRes.stats);
      } catch (err) {
        console.error('Error loading purchase history:', err);
        setError('Failed to load purchase history');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-10 w-10 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-medium text-gray-500">Total Orders</h3>
            <p className="text-2xl font-bold text-blue-600 mt-1">{stats.totalOrders}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-medium text-gray-500">Total Spent</h3>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatPrice(stats.totalSpent)}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-medium text-gray-500">Average Order</h3>
            <p className="text-2xl font-bold text-purple-600 mt-1">
              {formatPrice(stats.averageOrderValue)}
            </p>
          </div>
        </div>
      )}

      {orders.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center shadow-sm">
          <h2 className="text-xl font-semibold mb-2">No purchases yet</h2>
          <p className="text-gray-600 mb-6">
            Browse Artozphere to find artworks and complete your first purchase.
          </p>
          <Link
            to="/artozphere"
            className="inline-block rounded-lg bg-blue-600 px-5 py-2 text-white text-sm font-medium hover:bg-blue-700"
          >
            Browse Artozphere
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order._id} className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-gray-900">Order #{order._id.slice(-8)}</h3>
                  <p className="text-sm text-gray-500">Ordered on {formatDate(order.createdAt)}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">{formatPrice(order.totalPrice)}</p>
                  <span
                    className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}
                  >
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                </div>
              </div>

              <div className="px-5 py-4 space-y-3">
                {order.items.map((item, index) => {
                  const artwork = item.artwork;
                  const image = getArtworkImageSrc(artwork);
                  const detailPath = artwork
                    ? `/artozphere/artwork/${artwork.slug || artwork._id}`
                    : null;

                  return (
                    <div key={index} className="flex items-center gap-4">
                      {image ? (
                        detailPath ? (
                          <Link to={detailPath}>
                            <img
                              src={image}
                              alt={artwork?.title || 'Artwork'}
                              className="w-16 h-16 object-cover rounded-lg"
                            />
                          </Link>
                        ) : (
                          <img
                            src={image}
                            alt={artwork?.title || 'Artwork'}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        )
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                          No image
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        {detailPath ? (
                          <Link
                            to={detailPath}
                            className="font-medium text-gray-900 hover:text-blue-600"
                          >
                            {artwork?.title || 'Unavailable artwork'}
                          </Link>
                        ) : (
                          <p className="font-medium text-gray-900">
                            {artwork?.title || 'Unavailable artwork'}
                          </p>
                        )}
                        <p className="text-sm text-gray-500">
                          by{' '}
                          {artwork?.artist?.username ||
                            artwork?.artist_email ||
                            'Unknown Artist'}
                        </p>
                      </div>
                      <p className="font-medium text-gray-900">{formatPrice(item.price)}</p>
                    </div>
                  );
                })}
              </div>

              {order.shippingAddress && (
                <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 text-sm text-gray-600">
                  <p className="font-medium text-gray-800 mb-1">Shipping address</p>
                  <p>
                    {order.shippingAddress.fullName}
                    <br />
                    {order.shippingAddress.addressLine1}
                    <br />
                    {order.shippingAddress.city}, {order.shippingAddress.postalCode}
                    <br />
                    {order.shippingAddress.country}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyPurchasesTab;
